<template>
  <FlexCol class="relative" gap-1 ref="triggerRef">
    <Label :label="label" :required="required" :error="error" v-if="label" />
    <Input v-model="localValue" :error="error" :placeholder="placeholder ? placeholder : 'Selecione a data'" :disabled="props.disabled" mask="##/##/####">
      <template #pre-icon>
        <IconInput :disabled="disabled" icon="fa-calendar-days" @click="toggle" class="border-r border-slate-300" />
      </template>
      <template #pos-icon>
        <IconInput :disabled="disabled" icon="fa-xmark" class="border-l border-slate-300" @click="clear()" v-if="localValue" />
      </template>
    </Input>
    <Error :message="error" v-if="error" />
    <Teleport to="body">
      <div v-if="open" :style="popupStyle" class="fixed z-[2000]">
        <Calendar v-model="date" />
      </div>
    </Teleport>
  </FlexCol>
</template>

<script setup>
import {watch, ref, onMounted, onBeforeUnmount, nextTick} from "vue"
import {useUtils} from "@/composables/useUtils"
import {useFormat} from "@/composables/useFormat"
const {isValidDateBR} = useUtils()
const {formatDate, formatIsoDatetime, formatBrDateToIso} = useFormat()

const props = defineProps({
  label: String,
  required: Boolean,
  disabled: Boolean,
  error: Array,
  modelValue: [String, Number],
  placeholder: String,
})

const emits = defineEmits(["update:modelValue", "calendar-toggle"])
const localValue = ref(props.modelValue ? formatDate(props.modelValue) : null)

const error = ref(props.error)
const open = ref(false)
const isFocus = ref(false)
const date = ref(props.modelValue)
const triggerRef = ref(null)
const popupStyle = ref({})

const updatePopupPosition = () => {
  const triggerElement = triggerRef.value?.$el ?? triggerRef.value
  if (!open.value || !triggerElement || typeof triggerElement.getBoundingClientRect !== "function") return
  const rect = triggerElement.getBoundingClientRect()
  popupStyle.value = {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  }
}

const toggle = () => {
  if (!props.disabled) {
    open.value = !open.value
    if (open.value) {
      nextTick(updatePopupPosition)
    }
    emits("calendar-toggle", open.value)
  }
}

const clear = () => {
  if (!props.disabled) {
    localValue.value = null
    date.value = null
    emits("update:modelValue", null)
    return toggle()
  }
}

onMounted(() => {
  if (props.modelValue) {
    emits("update:modelValue", formatIsoDatetime(props.modelValue))
  }
  window.addEventListener("resize", updatePopupPosition)
  window.addEventListener("scroll", updatePopupPosition, true)
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", updatePopupPosition)
  window.removeEventListener("scroll", updatePopupPosition, true)
})

watch(date, (newValue) => {
  emits("update:modelValue", formatIsoDatetime(newValue))
  return toggle()
})

watch(open, (isOpen) => {
  if (isOpen) {
    nextTick(updatePopupPosition)
  }
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      emits("update:modelValue", formatIsoDatetime(newValue))
    }
    localValue.value = formatDate(newValue)
  },
  {immediate: true, deep: true},
)

watch(
  () => props.error,
  (newValue) => {
    error.value = newValue
  },
)

watch(
  () => localValue.value,
  (newValue) => {
    if (newValue && newValue.length === 10 && isValidDateBR(newValue)) {
      const rawValue = formatBrDateToIso(newValue)

      if (rawValue) {
        emits("update:modelValue", formatIsoDatetime(rawValue))
      }
    }
  },
)
</script>
